import React, { useState, useEffect } from 'react';
import { api } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { 
  X, Share2, Copy, Check, Sparkles, FileText, Zap, 
  RefreshCw, Edit2, Loader2, MessageCircle, ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';

// Format price in L or Cr
const formatPrice = (lakh) => {
  if (!lakh || lakh === 0) return '—';
  if (lakh >= 100) return `₹${(lakh / 100).toFixed(lakh % 100 === 0 ? 0 : 1)}Cr`;
  return `₹${lakh}L`;
};

const SmartShareModal = ({ 
  isOpen, 
  onClose, 
  projectId, 
  projectName,
  customerId = null,
  customerName = null,
  onShare 
}) => {
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [shareData, setShareData] = useState(null);
  const [message, setMessage] = useState('');
  const [mode, setMode] = useState('detailed'); // 'quick' or 'detailed'
  const [isEditing, setIsEditing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [usps, setUsps] = useState([]);
  const [generatingUsps, setGeneratingUsps] = useState(false);
  const [showModeSelector, setShowModeSelector] = useState(false);

  useEffect(() => {
    if (isOpen && projectId) {
      fetchShareData();
    }
  }, [isOpen, projectId]);

  useEffect(() => {
    if (shareData && !loading) {
      generateMessage();
    }
  }, [mode, shareData]);

  const fetchShareData = async () => {
    setLoading(true);
    try {
      const response = await api.getProjectShareData(projectId);
      setShareData(response.data);
      setUsps(response.data.usps || response.data.highlights || []);
      
      // Auto-generate USPs if none exist
      if (!response.data.usps?.length && !response.data.highlights?.length) {
        await generateUSPs();
      }
    } catch (error) {
      console.error('Error fetching share data:', error);
      toast.error('Failed to load project data');
    } finally {
      setLoading(false);
    }
  };

  const generateUSPs = async (forceRegenerate = false) => {
    setGeneratingUsps(true);
    try {
      const response = await api.generateProjectUSPs(projectId, forceRegenerate);
      setUsps(response.data.usps);
      if (shareData) {
        setShareData({ ...shareData, usps: response.data.usps });
      }
      toast.success(response.data.source === 'generated' ? 'USPs generated!' : 'USPs loaded');
    } catch (error) {
      console.error('Error generating USPs:', error);
      toast.error('Failed to generate USPs');
    } finally {
      setGeneratingUsps(false);
    }
  };

  const generateMessage = async () => {
    if (!shareData) return;
    
    setGenerating(true);
    try {
      const response = await api.generateShareMessage(
        projectId, 
        customerId, 
        mode,
        false // Don't use AI intro by default
      );
      setMessage(response.data.message);
      setIsEditing(false);
    } catch (error) {
      console.error('Error generating message:', error);
      toast.error('Failed to generate message');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(message);
    setCopied(true);
    toast.success('Message copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleWhatsAppShare = () => {
    const encodedMessage = encodeURIComponent(message.trim());
    window.open(`https://wa.me/?text=${encodedMessage}`, '_blank');
    toast.success('Opening WhatsApp...');
    onShare?.();
  };

  const handleModeChange = (newMode) => {
    setMode(newMode);
    setShowModeSelector(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center p-0 md:p-4">
      <div className="bg-white rounded-t-2xl md:rounded-2xl w-full max-w-lg max-h-[95vh] md:max-h-[85vh] overflow-hidden shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 p-4 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-[#5B3DF5] to-[#7C5DFA]">
          <div className="text-white">
            <h3 className="font-bold text-lg">Share with Customer</h3>
            <p className="text-xs text-white/80">{projectName}</p>
          </div>
          <button 
            onClick={onClose} 
            className="p-2 hover:bg-white/20 rounded-full transition-colors text-white"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin text-[#5B3DF5]" size={32} />
            </div>
          ) : (
            <>
              {/* Mode Selector */}
              <div className="relative">
                <button
                  onClick={() => setShowModeSelector(!showModeSelector)}
                  className="w-full flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-200 hover:border-[#5B3DF5] transition-colors"
                >
                  <div className="flex items-center gap-3">
                    {mode === 'quick' ? (
                      <Zap size={18} className="text-amber-500" />
                    ) : (
                      <FileText size={18} className="text-[#5B3DF5]" />
                    )}
                    <div className="text-left">
                      <p className="font-semibold text-sm text-slate-800">
                        {mode === 'quick' ? 'Quick Share' : 'Detailed Advisory'}
                      </p>
                      <p className="text-xs text-slate-500">
                        {mode === 'quick' 
                          ? 'Short summary with USPs + price' 
                          : 'Full version with all details'}
                      </p>
                    </div>
                  </div>
                  <ChevronDown size={18} className={`text-slate-400 transition-transform ${showModeSelector ? 'rotate-180' : ''}`} />
                </button>
                
                {showModeSelector && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl border border-slate-200 shadow-lg z-10 overflow-hidden">
                    <button
                      onClick={() => handleModeChange('quick')}
                      className={`w-full flex items-center gap-3 p-3 hover:bg-slate-50 ${mode === 'quick' ? 'bg-purple-50' : ''}`}
                    >
                      <Zap size={18} className="text-amber-500" />
                      <div className="text-left">
                        <p className="font-semibold text-sm">Quick Share</p>
                        <p className="text-xs text-slate-500">Short summary with USPs + price + CTA</p>
                      </div>
                      {mode === 'quick' && <Check size={16} className="ml-auto text-[#5B3DF5]" />}
                    </button>
                    <button
                      onClick={() => handleModeChange('detailed')}
                      className={`w-full flex items-center gap-3 p-3 hover:bg-slate-50 ${mode === 'detailed' ? 'bg-purple-50' : ''}`}
                    >
                      <FileText size={18} className="text-[#5B3DF5]" />
                      <div className="text-left">
                        <p className="font-semibold text-sm">Detailed Advisory</p>
                        <p className="text-xs text-slate-500">Full version with USPs + Details + Payment</p>
                      </div>
                      {mode === 'detailed' && <Check size={16} className="ml-auto text-[#5B3DF5]" />}
                    </button>
                  </div>
                )}
              </div>

              {/* Customer Context (if available) */}
              {customerName && (
                <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 rounded-lg border border-emerald-200">
                  <MessageCircle size={14} className="text-emerald-600" />
                  <p className="text-xs text-emerald-700">
                    Personalized for <span className="font-semibold">{customerName}</span>
                  </p>
                </div>
              )}

              {/* USPs Section */}
              <Card className="p-3 bg-amber-50/50 border-amber-200/50">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Sparkles size={14} className="text-amber-600" />
                    <span className="text-xs font-semibold text-amber-800">Project USPs</span>
                    {shareData?.usps_verified && (
                      <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">Verified</span>
                    )}
                  </div>
                  <button
                    onClick={() => generateUSPs(true)}
                    disabled={generatingUsps}
                    className="text-xs text-amber-700 hover:text-amber-900 flex items-center gap-1"
                  >
                    {generatingUsps ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <RefreshCw size={12} />
                    )}
                    Regenerate
                  </button>
                </div>
                <ul className="space-y-1">
                  {usps.length > 0 ? (
                    usps.map((usp, index) => (
                      <li key={index} className="flex items-start gap-2 text-xs text-amber-900">
                        <span className="text-amber-500 mt-0.5">•</span>
                        <span>{usp}</span>
                      </li>
                    ))
                  ) : (
                    <li className="text-xs text-amber-600 italic">No USPs generated yet</li>
                  )}
                </ul>
              </Card>

              {/* Message Preview */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Message Preview</span>
                  <div className="flex items-center gap-2">
                    {generating && (
                      <span className="text-xs text-slate-400 flex items-center gap-1">
                        <Loader2 size={12} className="animate-spin" />
                        Generating...
                      </span>
                    )}
                    <button
                      onClick={() => setIsEditing(!isEditing)}
                      className="text-xs text-[#5B3DF5] hover:text-[#4A2FE0] flex items-center gap-1"
                    >
                      <Edit2 size={12} />
                      {isEditing ? 'Done' : 'Edit'}
                    </button>
                  </div>
                </div>
                
                {isEditing ? (
                  <Textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    className="min-h-[300px] text-sm font-mono bg-slate-50 border-slate-200 focus:border-[#5B3DF5] focus:ring-[#5B3DF5]/20"
                    placeholder="Edit your message..."
                  />
                ) : (
                  <div className="bg-slate-50 rounded-xl p-4 text-sm whitespace-pre-wrap font-mono text-slate-700 border border-slate-200 max-h-[300px] overflow-y-auto">
                    {message || 'Generating message...'}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex-shrink-0 p-4 border-t border-slate-100 space-y-2 bg-white">
          <div className="grid grid-cols-2 gap-2">
            <Button 
              variant="outline" 
              onClick={handleCopy}
              disabled={!message || loading}
              className="h-11 rounded-xl"
            >
              {copied ? (
                <>
                  <Check size={16} className="mr-2 text-emerald-500" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy size={16} className="mr-2" />
                  Copy Text
                </>
              )}
            </Button>
            <Button 
              onClick={handleWhatsAppShare}
              disabled={!message || loading}
              className="h-11 bg-[#25D366] hover:bg-[#20BD5A] text-white rounded-xl font-bold"
            >
              <Share2 size={16} className="mr-2" />
              WhatsApp
            </Button>
          </div>
          <Button 
            variant="ghost" 
            onClick={onClose}
            className="w-full h-9 text-xs text-slate-500"
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SmartShareModal;
